import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth } from '../lib/adminApi';
import { api } from '../lib/api';
import type {
  CompanyLookupResponse,
  CompanySearchHit,
  LastAccountsResponse,
} from '@dormant/shared';

/**
 * Admin-only filing flow. Same end result as the public wizard
 * (/search -> /confirm -> /auth-code -> Stripe -> file with CH) but
 * collapsed into one page and bypassing Stripe. Used for:
 *
 *   - filing for customers who paid out-of-band (e.g. bank transfer)
 *   - making good on a rejected filing where we already refunded
 *   - filing the operator's own companies for free
 *
 * Reaches /api/admin/file-now which marks the order as PAID with
 * amountPence=0 and metadata.adminFiled=true so revenue analytics
 * remain clean.
 */
export function AdminFileNow() {
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CompanySearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState<CompanyLookupResponse | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  // Prior-year accounts fetched from CH's public register. Displayed
  // side-by-side with the current-year inputs so the admin can eyeball
  // the comparative column before submitting.
  const [priorAccounts, setPriorAccounts] = useState<LastAccountsResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [reason, setReason] = useState('');

  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const [figures, setFigures] = useState({
    fixedAssets: 0,
    currentAssets: 100,
    creditorsDueWithinOneYear: 0,
    creditorsDueAfterOneYear: 0,
    calledUpShareCapital: 100,
    profitAndLossAccount: 0,
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) navigate('/admin/login');
  }, [navigate]);

  // Debounced company search.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2 || lookup) {
      setHits([]);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const r = await api.searchCompanies(q.trim(), ctrl.signal);
        setHits(r.items || []);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  }, [q, lookup]);

  async function choose(hit: CompanySearchHit) {
    setLookupError(null);
    setHits([]);
    setQ(`${hit.title} (${hit.company_number})`);
    try {
      // Fetch lookup + last accounts in parallel so we can pre-fill the
      // balance sheet figures with the company's previous shape (paid /
      // cash vs unpaid / called-up-share-capital-not-paid). Without this
      // the admin gets the static £1 cash default and may end up filing
      // a balance sheet that doesn't match the company's history.
      const [res, last] = await Promise.all([
        api.lookupCompany(hit.company_number),
        api.lastAccounts(hit.company_number).catch(() => null),
      ]);
      setLookup(res);
      setPriorAccounts(last);
      if (res.nextAccounts) {
        setPeriodStart(res.nextAccounts.period_start);
        setPeriodEnd(res.nextAccounts.period_end);
      }
      const filed = last?.current?.balanceSheet;
      if (filed) {
        // Continuation: prior accounts available. If prior used the
        // "called up share capital not paid" layout, keep currentAssets
        // at 0 so the iXBRL generator picks the unpaid variant; share
        // capital comes from the equity line (or the not-paid line as a
        // fallback for templates that didn't tag both).
        const priorWasUnpaid =
          (filed.calledUpShareCapitalNotPaid ?? 0) > 0 &&
          (filed.currentAssets ?? 0) === 0;
        const shareCapital =
          filed.calledUpShareCapital ??
          filed.calledUpShareCapitalNotPaid ??
          last?.defaultShareCapital ??
          1;
        setFigures({
          fixedAssets: filed.fixedAssets ?? 0,
          currentAssets: priorWasUnpaid ? 0 : (filed.currentAssets ?? 0),
          creditorsDueWithinOneYear: filed.creditorsDueWithinOneYear
            ? -Math.abs(filed.creditorsDueWithinOneYear)
            : 0,
          creditorsDueAfterOneYear: filed.creditorsDueAfterOneYear
            ? -Math.abs(filed.creditorsDueAfterOneYear)
            : 0,
          calledUpShareCapital: shareCapital,
          profitAndLossAccount: 0,
        });
      } else if (last?.isFirstFiling) {
        // First filing default is UNPAID (share subscribed but never paid
        // in) — matches most genuinely dormant startups. Admin can flip
        // via the balance-sheet variant chip.
        const sc = last.defaultShareCapital ?? 1;
        setFigures({
          fixedAssets: 0,
          currentAssets: 0,
          creditorsDueWithinOneYear: 0,
          creditorsDueAfterOneYear: 0,
          calledUpShareCapital: sc,
          profitAndLossAccount: 0,
        });
      }
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Company lookup failed');
    }
  }

  function clearCompany() {
    setLookup(null);
    setPriorAccounts(null);
    setQ('');
    setHits([]);
    setPeriodStart('');
    setPeriodEnd('');
  }

  function setVariant(v: 'paid' | 'unpaid') {
    if (v === 'unpaid') {
      setFigures((f) => ({
        ...f,
        currentAssets: 0,
        calledUpShareCapital: f.calledUpShareCapital || f.currentAssets || 1,
      }));
    } else {
      setFigures((f) => ({
        ...f,
        currentAssets: f.calledUpShareCapital || 1,
      }));
    }
  }

  async function previewIxbrl() {
    if (!lookup) return;
    setPreviewBusy(true);
    setError(null);
    try {
      await adminApi.previewIxbrl({
        companyNumber: lookup.company.company_number,
        companyName: lookup.company.company_name,
        directorName: directorName.trim() || undefined,
        periodStart,
        periodEnd,
        isFirstFiling: lookup.isFirstFiling,
        figures: { ...figures, netAssets },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setPreviewBusy(false);
    }
  }

  // For the unpaid (called-up-share-capital-not-paid) variant the iXBRL
  // generator uses the share-capital amount as the asset cell — there is
  // no current-assets section. To keep the live "balanced?" indicator
  // honest in admin file-now we mirror that here: treat
  // calledUpShareCapital as the asset when currentAssets === 0.
  const isUnpaidVariant = figures.currentAssets === 0 && figures.calledUpShareCapital > 0;
  const assetSide = isUnpaidVariant
    ? figures.calledUpShareCapital + figures.fixedAssets
    : figures.fixedAssets + figures.currentAssets;
  const netAssets =
    assetSide -
    figures.creditorsDueWithinOneYear -
    figures.creditorsDueAfterOneYear;
  const equity = figures.calledUpShareCapital + figures.profitAndLossAccount;
  const balanced = netAssets === equity;

  const readyToFile =
    !!lookup &&
    /^[A-Za-z0-9]{6}$/.test(authCode) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    directorName.trim().length >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(periodStart) &&
    /^\d{4}-\d{2}-\d{2}$/.test(periodEnd) &&
    figures.calledUpShareCapital > 0 &&
    balanced;

  async function submit() {
    if (!lookup) return;
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.fileNow({
        email: email.trim(),
        companyNumber: lookup.company.company_number,
        companyName: lookup.company.company_name,
        directorName: directorName.trim(),
        isFirstFiling: lookup.isFirstFiling,
        periodStart,
        periodEnd,
        authCode: authCode.toUpperCase(),
        figures: { ...figures, netAssets },
        reason: reason.trim() || undefined,
      });
      navigate(`/admin/orders/${r.orderId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Filing failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900">File a dormant return — admin</h1>
      <p className="mt-1 text-sm text-slate-500">
        Skips Stripe. The filing is sent direct to Companies House at no
        charge and logged with <span className="font-mono text-xs">metadata.adminFiled = true</span>.
      </p>

      {/* Step 1 — pick the company */}
      <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">1. Company</h2>
        {lookup ? (
          <div className="mt-3 flex items-start justify-between gap-3 rounded-lg bg-slate-50 p-3 text-sm">
            <div>
              <div className="font-medium text-slate-900">{lookup.company.company_name}</div>
              <div className="text-xs text-slate-500">
                {lookup.company.company_number}
                {lookup.company.type ? ` · ${lookup.company.type}` : ''}
                {lookup.isFirstFiling ? ' · first filing' : ''}
              </div>
              {!lookup.eligibility.eligible && (
                <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                  <strong>Heads up:</strong> {lookup.eligibility.reason}. Admin filing
                  proceeds regardless — Companies House will still validate on its side.
                </div>
              )}
            </div>
            <button onClick={clearCompany} className="text-xs text-slate-500 hover:text-red-600">
              Change
            </button>
          </div>
        ) : (
          <div className="relative mt-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Acme Trading Ltd, or 12345678"
              className="input"
              autoFocus
            />
            {searching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                …
              </span>
            )}
            {hits.length > 0 && (
              <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                {hits.map((h) => (
                  <li
                    key={h.company_number}
                    onMouseDown={(e) => { e.preventDefault(); choose(h); }}
                    className="cursor-pointer border-b border-slate-100 px-4 py-2.5 hover:bg-brand-50 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-slate-900">{h.title}</div>
                    <div className="text-xs text-slate-500">
                      {h.company_number}
                      {h.date_of_creation && ` · incorporated ${h.date_of_creation}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {lookupError && (
          <div className="mt-3 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{lookupError}</div>
        )}
      </section>

      {/* Step 2 — period */}
      <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">2. Accounting period</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Period start">
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="input" />
          </Field>
          <Field label="Period end (ARD)">
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="input" />
          </Field>
        </div>
      </section>

      {/* Step 3 — balance sheet */}
      <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">3. Balance sheet figures</h2>

        {/* Explicit variant selector — replaces the old derived chip.
            Flipping this rearranges the iXBRL balance-sheet layout. */}
        <div className="mt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Share capital status
          </div>
          <div className="mt-1.5 inline-flex rounded-lg bg-slate-100 p-0.5">
            <button
              type="button"
              onClick={() => setVariant('paid')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                isUnpaidVariant
                  ? 'text-slate-500 hover:text-slate-700'
                  : 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
              }`}
            >
              Paid in cash
            </button>
            <button
              type="button"
              onClick={() => setVariant('unpaid')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                isUnpaidVariant
                  ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Subscribed but not paid
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {isUnpaidVariant
              ? 'Balance sheet will show "Called-up share capital not paid" as the asset (Current assets locked to £0).'
              : 'Balance sheet will show "Cash at bank and in hand" as the asset.'}
          </p>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <NumField label="Fixed assets" v={figures.fixedAssets} onChange={(v) => setFigures({ ...figures, fixedAssets: v })} />
          {isUnpaidVariant ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-400">Cash at bank</span>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400 italic">
                n/a (unpaid variant)
              </div>
            </div>
          ) : (
            <NumField label="Cash at bank" v={figures.currentAssets} onChange={(v) => setFigures({ ...figures, currentAssets: v })} />
          )}
          <NumField label="Creditors (≤ 1y)" v={figures.creditorsDueWithinOneYear} onChange={(v) => setFigures({ ...figures, creditorsDueWithinOneYear: v })} />
          <NumField label="Creditors (> 1y)" v={figures.creditorsDueAfterOneYear} onChange={(v) => setFigures({ ...figures, creditorsDueAfterOneYear: v })} />
          <NumField
            label={isUnpaidVariant ? 'Called-up share capital (not paid)' : 'Called-up share capital'}
            v={figures.calledUpShareCapital}
            onChange={(v) => setFigures({ ...figures, calledUpShareCapital: v })}
          />
          <NumField label="P&L account" v={figures.profitAndLossAccount} onChange={(v) => setFigures({ ...figures, profitAndLossAccount: v })} />
        </div>
        <div className="mt-4 grid gap-3 rounded-md bg-slate-50 p-3 text-sm sm:grid-cols-3">
          <Stat label="Net assets" value={`£${netAssets.toLocaleString('en-GB')}`} />
          <Stat label="Equity" value={`£${equity.toLocaleString('en-GB')}`} />
          <Stat label="Balanced" value={balanced ? '✅ yes' : '❌ no'} />
        </div>
        {!balanced && (
          <p className="mt-2 text-xs text-amber-700">
            Net assets must equal equity. Adjust figures before filing.
          </p>
        )}
      </section>

      {/* Step 3b — prior-year comparative from CH's public register */}
      {priorAccounts && !priorAccounts.isFirstFiling && priorAccounts.current?.balanceSheet && (
        <section className="mt-6 rounded-xl bg-slate-50 p-5 ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-900">
              3b. Prior-year comparative (from Companies House)
            </h2>
            <span className="text-xs text-slate-500">
              Period end {priorAccounts.current.endDate}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Read-only. This is what will render as the prior-year column in your iXBRL.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <PriorRow k="Fixed assets" cy={figures.fixedAssets} py={priorAccounts.current.balanceSheet.fixedAssets} />
            <PriorRow k="Cash at bank" cy={isUnpaidVariant ? 0 : figures.currentAssets} py={priorAccounts.current.balanceSheet.currentAssets} />
            <PriorRow k="Creditors (≤ 1y)" cy={figures.creditorsDueWithinOneYear} py={priorAccounts.current.balanceSheet.creditorsDueWithinOneYear} negatePy />
            <PriorRow k="Creditors (> 1y)" cy={figures.creditorsDueAfterOneYear} py={priorAccounts.current.balanceSheet.creditorsDueAfterOneYear} negatePy />
            <PriorRow k="Net assets" cy={netAssets} py={priorAccounts.current.balanceSheet.netAssets} bold />
            <PriorRow
              k="Share capital"
              cy={figures.calledUpShareCapital}
              py={
                priorAccounts.current.balanceSheet.calledUpShareCapital ??
                priorAccounts.current.balanceSheet.calledUpShareCapitalNotPaid
              }
            />
            <PriorRow k="P&L account" cy={figures.profitAndLossAccount} py={priorAccounts.current.balanceSheet.profitAndLossAccount} />
          </div>
          <button
            type="button"
            onClick={previewIxbrl}
            disabled={previewBusy || !balanced}
            className="mt-4 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {previewBusy ? 'Rendering…' : '👁 Preview iXBRL (opens in new tab)'}
          </button>
          <p className="mt-2 text-xs text-slate-500">
            Preview renders exactly what will be submitted, with the prior-year comparative fetched from Companies House.
          </p>
        </section>
      )}

      {/* First-filing companies: no prior data to compare against, but
          still offer the preview button. */}
      {priorAccounts?.isFirstFiling && lookup && (
        <section className="mt-6 rounded-xl bg-slate-50 p-5 ring-1 ring-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">3b. First filing</h2>
          <p className="mt-1 text-xs text-slate-500">
            No prior accounts on the Companies House register — the iXBRL will show only the current year with no comparative column.
          </p>
          <button
            type="button"
            onClick={previewIxbrl}
            disabled={previewBusy || !balanced}
            className="mt-3 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {previewBusy ? 'Rendering…' : '👁 Preview iXBRL (opens in new tab)'}
          </button>
        </section>
      )}

      {/* Step 4 — auth + filer */}
      <section className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">4. Authentication &amp; filer</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Companies House auth code">
            <input
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="••••••"
              className="input text-center text-lg tracking-[0.4em]"
            />
          </Field>
          <Field label="Director name (signs the accounts)">
            <input value={directorName} onChange={(e) => setDirectorName(e.target.value)} className="input" />
          </Field>
          <Field label="Customer email (for record)">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="director@example.com"
              className="input"
            />
          </Field>
          <Field label="Internal reason (optional)">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. comp filing for rejected order" className="input" />
          </Field>
        </div>
      </section>

      {error && (
        <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={submit}
          disabled={!readyToFile || busy}
          className="rounded-md bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:bg-slate-300"
        >
          {busy ? 'Filing…' : 'File with Companies House (free)'}
        </button>
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumField({
  label,
  v,
  onChange,
}: { label: string; v: number; onChange: (v: number) => void }) {
  return (
    <Field label={label}>
      <input
        type="number"
        value={v}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="input tabular-nums"
      />
    </Field>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

/**
 * Two-column row used inside the prior-year comparative panel. Left
 * column: what CH currently has on file for the last accounts (prior
 * year). Right column: what we're about to submit for the current year.
 * If the numbers diverge in an unexpected way we make the delta
 * visible so the admin catches typos.
 */
function PriorRow({
  k,
  cy,
  py,
  negatePy = false,
  bold = false,
}: {
  k: string;
  cy: number;
  py: number | undefined;
  negatePy?: boolean;
  bold?: boolean;
}) {
  // CH parser stores creditor amounts as negative on the balance
  // sheet; for the comparative panel we flip them positive so both
  // columns are directly comparable.
  const pyDisplay = py == null ? '—' : `£${(negatePy ? Math.abs(py) : py).toLocaleString('en-GB')}`;
  return (
    <>
      <div className={`text-slate-500 ${bold ? 'font-semibold' : ''}`}>{k}</div>
      <div className={`text-right tabular-nums text-slate-900 ${bold ? 'font-semibold' : ''}`}>
        <span className="text-xs text-slate-400 mr-3">PY {pyDisplay}</span>
        <span>CY £{cy.toLocaleString('en-GB')}</span>
      </div>
    </>
  );
}
